import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, SignIn } from '@clerk/clerk-react';
import { Users, Shield, Mail, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toast';
import {
  useInvitationByToken,
  useAcceptInvitation,
  storePendingInvitationToken,
} from '@/hooks/useInvitations';

// Check if we're in dev mode
const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function isExpired(dateStr: string): boolean {
  return new Date(dateStr) < new Date();
}

/**
 * Page for accepting team invitations
 */
export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const isSignedIn = isDevMode ? true : auth.isSignedIn;
  const isLoaded = isDevMode ? true : auth.isLoaded;
  const [showSignIn, setShowSignIn] = useState(false);

  const { data: invitation, isLoading, error } = useInvitationByToken(token ?? null);
  const acceptMutation = useAcceptInvitation();

  // Store token for auto-accept if user needs to sign up
  useEffect(() => {
    if (token && isLoaded && !isSignedIn) {
      storePendingInvitationToken(token);
    }
  }, [token, isLoaded, isSignedIn]);

  const handleAccept = async () => {
    if (!token) return;

    try {
      await acceptMutation.mutateAsync(token);
      toast.success('Welcome to the team!');

      // Navigate to the projects page after accepting
      setTimeout(() => {
        navigate('/projects');
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept invitation';
      toast.error(message);
    }
  };

  // Loading state
  if (isLoading || !isLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state (invitation not found or expired)
  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle>Invitation Not Found</CardTitle>
              <CardDescription>
                This invitation link is invalid or has expired. Please ask the team admin to send a new invitation.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button asChild>
                <Link to="/login">Go to Login</Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Check if invitation is expired
  if (isExpired(invitation.expires_at)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
              <CardTitle>Invitation Expired</CardTitle>
              <CardDescription>
                This invitation expired on {formatDate(invitation.expires_at)}. Please ask the team admin to send a new invitation.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button asChild>
                <Link to="/login">Go to Login</Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Show sign in if not authenticated
  if (!isSignedIn) {
    if (showSignIn) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-bold">Sign in to accept invitation</h1>
              <p className="text-muted-foreground mt-2">
                You're invited to join <strong>{invitation.team_name}</strong>
              </p>
            </div>
            <SignIn
              routing="hash"
              signUpUrl="/signup"
              forceRedirectUrl={`/invite/${token}`}
            />
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={() => setShowSignIn(false)}>
                Back to invitation details
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <CardTitle>You're Invited!</CardTitle>
              <CardDescription>
                You've been invited to join a team on KeyEnv
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Team</p>
                    <p className="font-medium">{invitation.team_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Invited by</p>
                    <p className="font-medium">{invitation.invited_by_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Your role</p>
                    <Badge variant={invitation.role === 'admin' ? 'default' : 'outline'}>
                      {invitation.role === 'admin' ? 'Admin' : 'Member'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button className="w-full" onClick={() => setShowSignIn(true)}>
                  Sign in to Accept
                </Button>
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/signup">Create Account</Link>
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Expires {formatDate(invitation.expires_at)}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Authenticated user - show accept button
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>Join {invitation.team_name}</CardTitle>
            <CardDescription>
              {invitation.invited_by_email} has invited you to join their team
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Your role</span>
                <Badge variant={invitation.role === 'admin' ? 'default' : 'outline'}>
                  {invitation.role === 'admin' ? 'Admin' : 'Member'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Invited as</span>
                <span className="font-medium">{invitation.email}</span>
              </div>
            </div>

            {acceptMutation.isSuccess ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
                <p className="text-center font-medium">Successfully joined the team!</p>
                <p className="text-sm text-muted-foreground">Redirecting...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={handleAccept}
                  disabled={acceptMutation.isPending}
                >
                  {acceptMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    'Accept Invitation'
                  )}
                </Button>
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/projects">Maybe Later</Link>
                </Button>
              </div>
            )}

            <p className="text-xs text-center text-muted-foreground">
              Expires {formatDate(invitation.expires_at)}
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
